import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useEffect, useState } from 'react';
import { GetStatisticsByDateConfig } from 'src/service/api';
import { Spin } from 'antd';

ChartJS.register(...registerables);

interface DailyStatItem {
  maleCount: number;
  femaleCount: number;
  total: number;
  statDate: string;
}

interface MonthlyReportCardProps {
  selectedQuizRuleId: number | null;
}

const MonthlyReportCard = ({ selectedQuizRuleId }: MonthlyReportCardProps) => {
  const [dailyStats, setDailyStats] = useState<DailyStatItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Get current month name
  const monthNames = [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
    'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
  ];
  const currentMonth = monthNames[new Date().getMonth()];

  // Fetch daily statistics
  useEffect(() => {
    const fetchDailyStats = async () => {
      if (!selectedQuizRuleId) return;
      
      setLoading(true);
      try {
        const res = await GetStatisticsByDateConfig({ quizRuleId: selectedQuizRuleId });
        if (res?.data && Array.isArray(res.data)) {
          // Sort by date
          const sortedData = res.data.sort((a: DailyStatItem, b: DailyStatItem) => 
            new Date(a.statDate).getTime() - new Date(b.statDate).getTime()
          );
          setDailyStats(sortedData);
        }
      } catch (error) {
        console.error('Failed to fetch daily stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDailyStats();
  }, [selectedQuizRuleId]);

  // Format date labels
  const dayLabels = dailyStats.map(item => {
    const date = new Date(item.statDate);
    return `${date.getDate()}`;
  });

  // Build datasets
  const datasets: any[] = [
    {
      type: 'line',
      label: 'Jami',
      data: dailyStats.map(item => item.total),
      borderColor: '#10b981',
      backgroundColor: (context: any) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        if (!chartArea) {
          return 'rgba(16, 185, 129, 0.1)';
        }
        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, 'rgba(16, 185, 129, 0.1)');
        gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.2)');
        gradient.addColorStop(1, 'rgba(16, 185, 129, 0.3)');
        return gradient;
      },
      borderWidth: 3,
      fill: true,
      tension: 0.5,
      pointRadius: 3,
      pointHoverRadius: 5,
      pointBackgroundColor: '#10b981',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
      order: 1
    },
    {
      type: 'bar',
      label: 'Erkaklar',
      data: dailyStats.map(item => item.maleCount),
      backgroundColor: 'rgba(59, 130, 246, 0.8)',
      borderRadius: 4,
      barThickness: 8,
      order: 2
    },
    {
      type: 'bar',
      label: 'Ayollar',
      data: dailyStats.map(item => item.femaleCount),
      backgroundColor: 'rgba(236, 72, 153, 0.8)',
      borderRadius: 4,
      barThickness: 8,
      order: 3
    }
  ];

  const data = {
    labels: dayLabels,
    datasets: datasets
  };

  const options = {
    indexAxis: 'x' as const,
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 2000,
      easing: 'easeOutQuart' as const,
      delay: (context: any) => {
        let delay = 0;
        if (context.type === 'data' && context.mode === 'default') {
          delay = context.dataIndex * 30;
        }
        return delay;
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const
    },
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          borderRadius: 3,
          padding: 15,
          font: {
            size: 13,
            weight: 500
          },
          color: '#64748b'
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#1e293b',
        padding: 12,
        borderRadius: 8,
        titleColor: '#fff',
        bodyColor: '#fff',
        titleFont: {
          size: 14,
          weight: 600
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          title: function (context: any) {
            const index = context[0].dataIndex;
            if (dailyStats[index]) {
              const date = new Date(dailyStats[index].statDate);
              return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            }
            return '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          },
          color: '#64748b'
        }
      },
      y: {
        grid: {
          color: '#f1f5f9',
          drawBorder: false
        },
        border: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#64748b'
        }
      }
    }
  };

  return (
    <div className="chart-card monthly-report-card">
      <div className="chart-header">
        <h3>Kunlik hisobot - {currentMonth}</h3>
      </div>
      <div className="chart-body">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin />
          </div>
        ) : dailyStats.length > 0 ? (
          <Chart type='bar' data={data} options={options} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748b' }}>
            Ma'lumot topilmadi
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthlyReportCard;
